<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <FaraidTopbar
        app-name="OnePlaceHQ"
        product-name="Faraid"
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        search-placeholder="Search cases, scholars..."
        @select-case="handleSelectCase"
        @notifications="handleNotifications"
        @settings="handleSettings"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="dashboard-view">
      <!-- License Error Banner -->
      <div v-if="licenseError" class="license-error-banner">
        <div class="license-error-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2>{{ licenseError.title }}</h2>
        <p>{{ licenseError.message }}</p>
        <IntercomButton variant="secondary" @click="handleLogout">
          Logout
        </IntercomButton>
      </div>

      <!-- Normal Dashboard Content -->
      <template v-else>
        <!-- Page Header -->
        <div class="page-header">
          <div>
            <h1 class="page-title">{{ $t('dashboard.title') }}</h1>
            <p class="page-subtitle">{{ $t('dashboard.subtitle') }}</p>
          </div>
          <IntercomButton variant="primary" @click="$router.push('/cases/new')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>{{ $t('dashboard.newCase') }}</span>
          </IntercomButton>
        </div>

        <!-- Stats Grid -->
        <div class="stats-grid">
          <IntercomCard class="stat-card">
            <div class="stat-label">{{ $t('dashboard.totalCases') }}</div>
            <div class="stat-value">{{ stats.total_count || 0 }}</div>
          </IntercomCard>

          <IntercomCard class="stat-card warning">
            <div class="stat-label">{{ $t('dashboard.pendingReview') }}</div>
            <div class="stat-value">{{ stats.pending_count || 0 }}</div>
          </IntercomCard>

          <IntercomCard class="stat-card success">
            <div class="stat-label">{{ $t('dashboard.evaluatedMonth') }}</div>
            <div class="stat-value">{{ stats.evaluated_count || 0 }}</div>
          </IntercomCard>

          <IntercomCard class="stat-card info">
            <div class="stat-label">{{ $t('dashboard.avgReviewHours') }}</div>
            <div class="stat-value">{{ (stats.avg_review_hours || 0).toFixed(1) }}h</div>
          </IntercomCard>
        </div>

        <!-- Secondary Stats -->
        <div class="secondary-stats">
          <span class="secondary-stat">
            {{ $t('dashboard.inReview') }}: <strong>{{ stats.in_review_count || 0 }}</strong>
          </span>
          <span class="secondary-stat">
            {{ $t('dashboard.awaitingInfo') }}: <strong>{{ stats.awaiting_info_count || 0 }}</strong>
          </span>
          <span class="secondary-stat">
            {{ $t('dashboard.delivered') }}: <strong>{{ stats.delivered_count || 0 }}</strong>
          </span>
        </div>

        <!-- Recent Cases -->
        <IntercomCard class="recent-section">
          <div class="section-header">
            <h3>{{ $t('dashboard.recentCases') }}</h3>
            <IntercomButton variant="ghost" size="sm" @click="$router.push('/cases')">
              {{ $t('dashboard.viewAll') }}
            </IntercomButton>
          </div>
          <IntercomTable
            :columns="caseColumns"
            :data="recentCases"
            :loading="isLoading"
            hoverable
            clickable
            @row-click="handleCaseClick"
          >
            <template #cell-status="{ value }">
              <CaseStatusBadge :status="value" />
            </template>
            <template #cell-submitted_at="{ value }">
              {{ formatDate(value) }}
            </template>
          </IntercomTable>
          <p v-if="!isLoading && recentCases.length === 0" class="empty-text">
            {{ $t('case.noCases') }}
          </p>
        </IntercomCard>
      </template>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useFaraid } from '../composables/useFaraid.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
} from '@shared/components/intercom'
import FaraidTopbar from '../components/FaraidTopbar.vue'
import CaseStatusBadge from '../components/CaseStatusBadge.vue'
import { useNavigation } from '../composables/useNavigation.js'
import dayjs from 'dayjs'

const { t } = useI18n()
const router = useRouter()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { getDashboard, listCases } = useFaraid()
const { navSections } = useNavigation()

// State
const stats = ref({})
const recentCases = ref([])
const isLoading = ref(false)
const licenseError = ref(null)

const formatDate = (date) => date ? dayjs(date).format('DD MMM YYYY') : ''

const caseColumns = [
  { key: 'case_number', title: t('case.caseNumber'), width: '130px' },
  { key: 'deceased_name', title: t('case.deceased'), width: '160px' },
  { key: 'school', title: t('case.school'), width: '100px' },
  { key: 'status', title: t('case.status'), width: '130px' },
  { key: 'submitted_at', title: t('case.date'), width: '120px' },
]

const fetchDashboard = async () => {
  isLoading.value = true
  licenseError.value = null

  try {
    const [dashboardData, casesData] = await Promise.all([
      getDashboard().catch((err) => {
        if (err.errorCode === 'NO_LICENSE' || err.isLicenseError) {
          licenseError.value = {
            title: 'No License',
            message: err.detail || 'No valid license for faraid module. Please contact your administrator.',
          }
        }
        return {}
      }),
      listCases({ limit: 5 }).catch(() => ({ items: [] })),
    ])

    stats.value = dashboardData || {}
    recentCases.value = casesData?.items || []
  } catch (error) {
    console.error('Failed to fetch dashboard:', error)
  } finally {
    isLoading.value = false
  }
}

const handleCaseClick = (row) => router.push(`/cases/${row.id}`)
const handleSelectCase = (c) => router.push(`/cases/${c.id}`)
const handleNotifications = () => console.log('Notifications')
const handleSettings = () => router.push('/settings')

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusiness()
  fetchDashboard()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.page-header {
  margin-bottom: var(--space-8);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

/* License Error Banner */
.license-error-banner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  padding: var(--space-10);
  background: var(--color-gray-50);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-gray-200);
}

.license-error-icon {
  color: var(--color-warning-500);
  margin-bottom: var(--space-6);
}

.license-error-banner h2 {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-3) 0;
}

.license-error-banner p {
  font-size: var(--font-size-base);
  color: var(--color-gray-600);
  margin: 0 0 var(--space-6) 0;
  max-width: 400px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-5);
  margin-bottom: var(--space-4);
}

.stat-card {
  padding: var(--space-5);
  text-align: center;
}

.stat-label {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin-bottom: var(--space-2);
}

.stat-value {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.stat-card.warning .stat-value { color: #b45309; }
.stat-card.success .stat-value { color: #059669; }
.stat-card.info .stat-value { color: #2563eb; }

.secondary-stats {
  display: flex;
  gap: var(--space-6);
  margin-bottom: var(--space-8);
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
}

.secondary-stat strong {
  color: var(--color-charcoal);
}

.recent-section {
  margin-bottom: var(--space-6);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-4);
}

.section-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 600;
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

@media (max-width: 768px) {

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-3);
  }

  .secondary-stats {
    flex-direction: column;
    gap: var(--space-2);
  }
}
</style>
